import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { initialMessage, userMessage } from 'constants/ui.constants';
import { contactPermissionHebrew, generalBankAccountTranslation, generalTranslation } from 'constants/global.constants';

interface ICloseAccountVerificationResult {
	isCompanyHasPendingFinancialRequest: boolean;
	isCompanyHasBalance: boolean;
	contactsWithPermissions: any[] | null;
}
interface IPaginationState {
	hasMore: boolean;
	total: number;
}

const initialPaginationState: IPaginationState = { hasMore: false, total: 0 };

interface ICustomerAccountsState {
	companyDashboard: any | null;
	beneficiaries: any[];
	beneficiariesPagination: IPaginationState;
	moneyActivities: any[];
	moneyActivitiesPagination: IPaginationState;
	bankAccounts: any[];
	bankAccountsPagination: IPaginationState;
	supportRequests: any[];
	supportRequestsPagination: IPaginationState;
	balances: any[];
	balancesPagination: IPaginationState;
	beneficiaryPayments: any[];
	beneficiaryPaymentsPagination: IPaginationState;
	gridTab: number;
	closeAccountVerificationResult: ICloseAccountVerificationResult | null;
	loading: boolean;
	successfullyUpdated: boolean;
	error: userMessage;
	selectedEntity: {
		type: string | null;
		data: any | null;
	} | null;
}

const initialState: ICustomerAccountsState = {
	companyDashboard: null,
	beneficiaries: [],
	beneficiariesPagination: initialPaginationState,
	moneyActivities: [],
	moneyActivitiesPagination: initialPaginationState,
	bankAccounts: [],
	bankAccountsPagination: initialPaginationState,
	supportRequests: [],
	supportRequestsPagination: initialPaginationState,
	balances: [],
	balancesPagination: initialPaginationState,
	beneficiaryPayments: [],
	beneficiaryPaymentsPagination: initialPaginationState,
	gridTab: 0,
	closeAccountVerificationResult: null,
	loading: false,
	successfullyUpdated: false,
	error: initialMessage,
	selectedEntity: null,
};

export const customerAccountsSlice = createSlice({
	name: 'customerAccount',
	initialState,
	reducers: {
		setCustomerDashboard: (state, { payload }) => {
			if (payload) {
				// Transform contacts to include Hebrew translations for table display
				const transformedContacts =
					payload.contacts?.map((contact: any) => ({
						...contact,
						statusHebrew: generalTranslation[contact.status],
						permissionHebrew: contactPermissionHebrew[contact.permission],
					})) || [];

				state.companyDashboard = {
					basicData: payload.basicData,
					pendingRequests: payload.pendingRequests,
					plan: payload.plan,
					nextMonthPlan: payload.nextMonthPlan,
					balances: payload.balances,
					lastCompletedPayment: payload.lastCompletedPayment,
					contacts: transformedContacts,
					bankAccounts: payload.bankAccounts,
				};
				state.beneficiaries = [];
				state.moneyActivities = [];
				state.bankAccounts = [];
				state.supportRequests = [];
				state.balances = [];
			} else {
				state.companyDashboard = null;
				state.beneficiaries = [];
				state.moneyActivities = [];
				state.bankAccounts = [];
				state.supportRequests = [];
				state.balances = [];
			}
		},
		setBeneficiaries: (state, { payload }) => {
			const transformed =
				payload.data?.map((beneficiary: any) => ({
					...beneficiary,
					statusHebrew: generalTranslation[beneficiary.status],
					typeHebrew: generalTranslation[beneficiary.type],
					paymentTypeHebrew: beneficiary.paymentType?.includes('/')
						? `${generalTranslation.Regular}/${generalTranslation.Priority}`
						: generalTranslation[beneficiary.paymentType],
				})) || [];
			state.beneficiaries = transformed;
			state.beneficiariesPagination = {
				hasMore: payload.pagination?.hasMore ?? false,
				total: payload.pagination?.total ?? 0,
			};
		},
		setMoneyActivities: (state, { payload }) => {
			const transformed =
				payload.data?.map((activity: any) => {
					let fee = {};
					if (activity.fee) {
						fee = {
							'fee.status': activity?.fee?.status,
							'fee.statusHebrew': generalTranslation[activity?.fee?.status],
							'fee.prevStatus': activity?.fee?.prevStatus,
							'fee.prevStatusHebrew': activity?.fee?.prevStatus
								? generalTranslation[activity?.fee?.prevStatus]
								: '',
							'fee.statusLastChangedByBoUser': activity?.fee?.statusLastChangedByBoUser
								? `${activity?.fee?.statusLastChangedByBoUser?.firstName} ${activity?.fee?.statusLastChangedByBoUser?.lastName}`
								: '',
							'fee.statusLastChanged': activity?.fee?.statusLastChanged,
						};
					}
					return {
						...fee,
						...activity,
						prevStatus: activity?.prevStatus,
						prevStatusHebrew: activity?.prevStatus ? generalTranslation[activity?.prevStatus] : '',
						statusLastChangedByBoUser: activity?.statusLastChangedByBoUser
							? `${activity?.statusLastChangedByBoUser?.firstName} ${activity?.statusLastChangedByBoUser?.lastName}`
							: '',
						statusLastChanged: activity?.statusLastChanged,
						typeHebrew: generalTranslation[activity?.balanceType || activity.type],
						statusHebrew: generalTranslation[activity.status],
						typeOfPaymentHebrew: generalTranslation[activity.typeOfPayment],
						swiftFeeTypeHebrew: generalTranslation[activity.fee?.swiftFeeType],
						reasonTitleHebrew: generalTranslation[activity.reasonTitle],
						beneficiaryStatusHebrew: generalTranslation[activity.beneficiary?.status],
					};
				}) || [];
			state.moneyActivities = transformed;
			state.moneyActivitiesPagination = {
				hasMore: payload.pagination?.hasMore ?? false,
				total: payload.pagination?.total ?? 0,
			};
		},
		setBankAccounts: (state, { payload }) => {
			const transformed =
				payload.data?.map((bankAccount: any) => ({
					...bankAccount,
					statusHebrew: generalBankAccountTranslation[bankAccount.status],
				})) || [];
			state.bankAccounts = transformed;
			state.bankAccountsPagination = {
				hasMore: payload.pagination?.hasMore ?? false,
				total: payload.pagination?.total ?? 0,
			};
		},
		setSupportRequests: (state, { payload }) => {
			state.supportRequests = payload.data || [];
			state.supportRequestsPagination = {
				hasMore: payload.pagination?.hasMore ?? false,
				total: payload.pagination?.total ?? 0,
			};
		},
		setBalances: (state, { payload }) => {
			state.balances = payload.data || [];
			state.balancesPagination = {
				hasMore: payload.pagination?.hasMore ?? false,
				total: payload.pagination?.total ?? 0,
			};
		},
		setBeneficiaryPayments: (state, { payload }) => {
			const transformed =
				payload.data?.map((payment: any) => ({
					...payment,
					type: 'Payment',
					statusHebrew: generalTranslation[payment.status],
					typeHebrew: generalTranslation['Payment'],
					typeOfPaymentHebrew: generalTranslation[payment.typeOfPayment],
				})) || [];
			state.beneficiaryPayments = transformed;
			state.beneficiaryPaymentsPagination = {
				hasMore: payload.pagination?.hasMore ?? false,
				total: payload.pagination?.total ?? 0,
			};
		},
		clearBeneficiaryPayments: (state) => {
			state.beneficiaryPayments = [];
			state.beneficiaryPaymentsPagination = initialPaginationState;
		},
		setGridTab: (state, { payload }) => {
			state.gridTab = payload;
		},
		setCloseAccountVerificationResult: (state, { payload }) => {
			state.closeAccountVerificationResult = payload;
		},
		setIsLoading: (state, { payload }) => {
			state.loading = payload;
		},
		setSuccessfullyUpdated: (state, { payload }) => {
			state.successfullyUpdated = payload;
		},
		setError: (state, action: PayloadAction<userMessage>) => {
			state.error = action.payload;
		},
		setSelectedEntity: (state, { payload }) => {
			if (payload === null) {
				state.selectedEntity = null;
				return;
			}

			const { type, data } = payload;
			let transformed = data;

			// Apply transformations based on type (matching list transformations)
			switch (type) {
				case 'contacts':
					if (data) {
						transformed = {
							...data,
							statusHebrew: generalTranslation[data.status],
							legalStatusHebrew: generalTranslation[data.legalStatus],
							identificationTypeHebrew: generalTranslation[data.identificationType],
							genderHebrew: generalTranslation[data.gender],
							permissionHebrew: contactPermissionHebrew[data.permission],
						};
					}
					break;

				case 'beneficiaries':
					if (data) {
						transformed = {
							...data,
							statusHebrew: generalTranslation[data.status],
							typeHebrew: generalTranslation[data.type],
							paymentTypeHebrew: data.paymentType?.includes('/')
								? `${generalTranslation.Regular}/${generalTranslation.Priority}`
								: generalTranslation[data.paymentType],
						};
					}
					break;

				case 'bankAccounts':
					if (data) {
						transformed = {
							...data,
							statusHebrew: generalBankAccountTranslation[data.status],
						};
					}
					break;

				case 'payment':
				case 'balance':
				case 'conversion':
				case 'balanceRefund':
					if (data) {
						let fee = {};
						if (data.fee) {
							fee = {
								'fee.status': data?.fee?.status,
								'fee.statusHebrew': generalTranslation[data?.fee?.status],
								'fee.prevStatus': data?.fee?.prevStatus,
								'fee.prevStatusHebrew': data?.fee?.prevStatus
									? generalTranslation[data?.fee?.prevStatus]
									: '',
								'fee.statusLastChangedByBoUser': data?.fee?.statusLastChangedByBoUser
									? `${data?.fee?.statusLastChangedByBoUser?.firstName} ${data?.fee?.statusLastChangedByBoUser?.lastName}`
									: '',
								'fee.statusLastChanged': data?.fee?.statusLastChanged,
							};
						}
						transformed = {
							...fee,
							...data,
							prevStatus: data?.prevStatus,
							prevStatusHebrew: data?.prevStatus ? generalTranslation[data?.prevStatus] : '',
							statusLastChangedByBoUser: data?.statusLastChangedByBoUser
								? `${data?.statusLastChangedByBoUser?.firstName} ${data?.statusLastChangedByBoUser?.lastName}`
								: '',
							statusLastChanged: data?.statusLastChanged,
							typeHebrew: generalTranslation[data?.balanceType || data.type],
							statusHebrew: generalTranslation[data.status],
							typeOfPaymentHebrew: generalTranslation[data.typeOfPayment],
							swiftFeeTypeHebrew: generalTranslation[data.fee?.swiftFeeType],
							reasonTitleHebrew: generalTranslation[data.reasonTitle],
							beneficiaryStatusHebrew: generalTranslation[data.beneficiary?.status],
						};
					}
					break;
			}

			state.selectedEntity = { type, data: transformed };
		},
	},
});

export const getSupportRequestById = (state: any, id: number) => {
	return state.customerAccount.supportRequests.find((supportRequest: any) => supportRequest.id === id);
};

export const getCompanyDashboard = (state: any) => state.customerAccount.companyDashboard;
export const getBeneficiaries = (state: any) => state.customerAccount.beneficiaries;
export const getBeneficiariesPagination = (state: any) => state.customerAccount.beneficiariesPagination;
export const getMoneyActivities = (state: any) => state.customerAccount.moneyActivities;
export const getMoneyActivitiesPagination = (state: any) => state.customerAccount.moneyActivitiesPagination;
export const getBankAccounts = (state: any) => state.customerAccount.bankAccounts;
export const getBankAccountsPagination = (state: any) => state.customerAccount.bankAccountsPagination;
export const getSupportRequests = (state: any) => state.customerAccount.supportRequests;
export const getSupportRequestsPagination = (state: any) => state.customerAccount.supportRequestsPagination;
export const getBalances = (state: any) => state.customerAccount.balances;
export const getBalancesPagination = (state: any) => state.customerAccount.balancesPagination;
export const getBeneficiaryPayments = (state: any) => state.customerAccount.beneficiaryPayments;
export const getBeneficiaryPaymentsPagination = (state: any) => state.customerAccount.beneficiaryPaymentsPagination;
export const getGridTab = (state: any) => state.customerAccount.gridTab;
export const getCloseAccountVerificationResult = (state: any): ICloseAccountVerificationResult =>
	state.customerAccount.closeAccountVerificationResult;
export const getLoading = (state: any) => state.customerAccount.loading;
export const getSuccessfullyUpdated = (state: any) => state.customerAccount.successfullyUpdated;
export const getError = (state: any) => state.customerAccount.error;
export const getSelectedEntity = (state: any) => state.customerAccount.selectedEntity;

export const {
	setCustomerDashboard,
	setBeneficiaries,
	setMoneyActivities,
	setBankAccounts,
	setSupportRequests,
	setBalances,
	setBeneficiaryPayments,
	clearBeneficiaryPayments,
	setGridTab,
	setCloseAccountVerificationResult,
	setIsLoading,
	setSuccessfullyUpdated,
	setError,
	setSelectedEntity,
} = customerAccountsSlice.actions;

export default customerAccountsSlice.reducer;
